import { AvatarSize } from "@components/Avatar";
import { ButtonRole, ButtonSize } from "@components/Button";
import { MarkdownText } from "@components/MarkdownText";
import {
  HeaderCardMetadataEntry,
  HeaderCardMetadataModalButton,
  LayoutDirection,
} from "@components/Profile/HeaderCardInfo";
import {
  ProfilePageHeaderCard,
  headerMetaWrapperStyles,
} from "@components/ProfilePageHeaderCard";
import { ShareButton } from "@components/ShareButton";
import { NonprofitTagSupporters } from "@components/SupportersList/NonprofitTagSupporters";
import { TagIllustrationOld } from "@components/TagIllustration";
import { getTagShareCloudinaryParams } from "@components/Tags/helpers";
import { LikeButton } from "@components/feed/LikeButton";
import { css } from "@emotion/react";
import styled from "@emotion/styled";
import { Big } from "big.js";
import React, { useMemo } from "react";

import { TagResponse } from "@every.org/common/src/codecs/entities";
import { LikeableType } from "@every.org/common/src/entity/types";
import {
  ClientRouteName,
  getRoutePath,
  URLFormat,
} from "@every.org/common/src/helpers/clientRoutes";

import { useLoggedInUserOrUndefined } from "src/context/AuthContext/hooks";
import { buttonCss as makeButtonCss } from "src/styles/button";
import { colorCssVars } from "src/theme/color";
import { cssForMediaSize, MediaSize } from "src/theme/mediaQueries";
import { horizontalStackCss, verticalStackCss } from "src/theme/spacing";
import { TextSize, textSizeCss, FontWeight } from "src/theme/text";
import { displayCurrencyValueInUserLocale } from "src/utility/currency";
import { shareTextForTag, getDescription } from "src/utility/nonprofitTags";

const ShareButtonStyled = styled(ShareButton)`
  ${cssForMediaSize({
    min: MediaSize.LARGE,
    css: makeButtonCss(ButtonRole.SECONDARY, ButtonSize.MEDIUM),
  })}
`;

const ShareText = styled.span`
  ${cssForMediaSize({
    max: MediaSize.MEDIUM,
    css: { display: "none" },
  })}
`;
interface TagHeaderCardProps {
  tag: TagResponse;
}

const HEADER_DESCRIPTION_ALLOWED_TAGS = ["a"];

// Only show these numbers when they are large and inspiring
const MIN_SUPPORTER_COUNT_TO_DISPLAY = 200;
const MIN_RAISED_TO_DISPLAY = 100 * 1000;

/**
 * Full page width card component that shows information about a cause.
 */
export const TagHeaderCard: React.FCC<TagHeaderCardProps> = React.memo(
  function TagHeaderCardImpl({ tag }) {
    const {
      tagName,
      tagImageCloudinaryId,
      title,
      description,
      supporterInfo,
      totalDonated: totalDonatedFromApi,
      shareText,
    } = tag;

    const totalDonated = totalDonatedFromApi
      ? {
          amount: new Big(totalDonatedFromApi.amount),
          currency: totalDonatedFromApi.currency,
        }
      : undefined;

    const supporterCount = supporterInfo?.numSupporters;
    const headerDescription = getDescription({
      description,
      title,
    });

    const shareImageCloudinaryParams = useMemo(
      () => getTagShareCloudinaryParams(tag),
      [tag]
    );

    const loggedInUser = useLoggedInUserOrUndefined();

    const showStats =
      totalDonated &&
      totalDonated.amount.gt(MIN_RAISED_TO_DISPLAY) &&
      typeof supporterCount === "number" &&
      supporterCount > MIN_SUPPORTER_COUNT_TO_DISPLAY;

    const likeButton = useMemo(
      () => (
        <LikeButton
          data-tname="likeButton"
          role={ButtonRole.TEXT_ONLY}
          size={ButtonSize.SMALL}
          type={LikeableType.TAG}
          likeCount={null}
          id={tag.id}
          loggedInUserLikes={
            !!loggedInUser?.nonprofitTagLikes?.includes(tag.id)
          }
        />
      ),
      [loggedInUser?.nonprofitTagLikes, tag.id]
    );
    return (
      <ProfilePageHeaderCard
        title={title}
        avatar={(avatarCss) => (
          <TagIllustrationOld
            tagImageCloudinaryId={tagImageCloudinaryId}
            tagName={tagName}
            size={AvatarSize.MEDIUM}
            customCSS={avatarCss}
            causeCategory={tag.causeCategory}
          />
        )}
        subtitle={
          headerDescription ? (
            <p
              css={css`
                color: var(${colorCssVars.text.secondary});
                ${textSizeCss[TextSize.s]};
                ${cssForMediaSize({
                  max: MediaSize.MEDIUM_LARGE,
                  css: { display: "none" },
                })};
              `}
            >
              <MarkdownText
                content={headerDescription}
                allowedTags={HEADER_DESCRIPTION_ALLOWED_TAGS}
              />
            </p>
          ) : undefined
        }
        // Arbitrarily chosen length cutoff for aligning items in the header
        alignTop={!!headerDescription && headerDescription.length > 256}
        metadata={
          <div css={headerMetaWrapperStyles}>
            {showStats && (
              <HeaderCardMetadataEntry
                data={displayCurrencyValueInUserLocale({
                  currencyValue: {
                    currency: totalDonated.currency,
                    amount: new Big(totalDonated.amount.toFixed(0)),
                  },
                })}
                label={"Raised"}
                labelFirst={false}
                layoutDirection={LayoutDirection.VERTICAL}
                entryCssOverride={css`
                  display: flex;
                  justify-content: center;

                  dt {
                    color: var(${colorCssVars.text.secondary});
                    ${textSizeCss[TextSize.xs]}
                  }
                  dd {
                    font-weight: ${FontWeight.BOLD};
                    ${textSizeCss[TextSize.s]};
                    ${cssForMediaSize({
                      min: MediaSize.MEDIUM,
                      css: textSizeCss[TextSize.m],
                    })};
                  }
                `}
              />
            )}
            {showStats && (
              <dl css={[verticalStackCss.xxs]}>
                <HeaderCardMetadataModalButton
                  modalButtonProps={{
                    buttonProps: {
                      "data-tname": `tag-${tagName}-supporters`,
                      role: ButtonRole.UNSTYLED,
                    },
                    modalHeader: "Donors",
                    modalContentLabel: "Donors Modal",
                    modalContent: function NonprofitTagSupportersModalContent(
                      setIsModalOpen
                    ) {
                      return (
                        <NonprofitTagSupporters
                          nonprofitTag={tagName}
                          supporterCount={supporterCount}
                          onClick={() => {
                            setIsModalOpen(false);
                          }}
                        />
                      );
                    },
                  }}
                  headerCardMetadataProps={{
                    data: supporterCount,
                    label: `Supporter${supporterCount === 1 ? "" : "s"}`,
                    labelFirst: false,
                    microdataInteractionType: "https://schema.org/DonateAction",
                  }}
                />
              </dl>
            )}
            <div
              css={[
                horizontalStackCss.xs,
                css`
                  margin-left: auto;
                  align-items: center;
                `,
                cssForMediaSize({
                  min: MediaSize.LARGE,
                  css: horizontalStackCss.s,
                }),
              ]}
            >
              {likeButton}
              <ShareButtonStyled
                data-tname={`share-${tagName}-button`}
                role={ButtonRole.TEXT_ONLY}
                size={ButtonSize.SMALL}
                shareData={{
                  text: shareTextForTag({ title, shareText }) || "",
                  url: getRoutePath({
                    name: ClientRouteName.NONPROFIT_OR_CAUSE,
                    tokens: { nonprofitSlug: tagName },
                    format: URLFormat.ABSOLUTE,
                  }),
                }}
                instagramImageCloudinaryParams={shareImageCloudinaryParams}
              >
                <ShareText>Share</ShareText>
              </ShareButtonStyled>
            </div>
          </div>
        }
      >
        {/* TODO: can it be unified to be showed in same place for mobile and desktop? */}
        {headerDescription && (
          <p
            css={css`
              color: var(${colorCssVars.text.secondary});
              ${textSizeCss[TextSize.s]};
              ${cssForMediaSize({
                min: MediaSize.LARGE,
                css: { display: "none" },
              })}
            `}
          >
            <MarkdownText
              content={headerDescription}
              allowedTags={HEADER_DESCRIPTION_ALLOWED_TAGS}
            />
          </p>
        )}
      </ProfilePageHeaderCard>
    );
  }
);
